/*
 * Authors: mariushirjanu, Koen, Triadi
 * Created: 11/10/2017
 * Upgraded: 29/08/2022
 */
import * as _ from 'underscore';
import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { IUserInfo, USERPROFILE_WIZARD, UserProfileService } from '../user-profile.service';
import { ICompany, IContactExtended, ICountry, ICountryExtended, ILanguage, ILocationExtended, ISubCompany, IValueObject } from '../../../../type_definitions/datatypes';
import { GaFormlyService, IExtraFieldConfigurationObject } from '../../../shared/formly/ga-formly.service';

import { IUserData, IUserWebshop } from '../../../shared/services/auth/auth-data.service';
import { AuthService } from '../../../shared/services/auth/auth.service';
import { LOV_UPDATE_METHOD, LovService } from '../../../shared/services/lov.service';
import { util } from '../../../shared/util';
import { UrlService } from '../../../shared/browser/url.service';
import { FormattingService } from '../../../shared/services/formatting.service';
import { CountryDataService } from '../../../shared/location/country-data.service';
import { CompanyDataService } from '../../../shared/company/company-data.service';
import { UserProfileDataService } from '../userprofile-data.service';
import { LanguageDataService } from '../../../shared/language/language-data.service';
import { UserDataService } from '../../../shared/user/user-data.service';
import { TranslationService } from '../../../shared/translation/translation.service';
import { GaFormlyValidatorService } from '../../../shared/formly/ga-formly-validator.service';
import { DialogSize, GaMessageService, IDialog } from '../../../shared/message/ga-message.service';
import { GaValidateService, IValidatingComponent, IValidationContext } from '../../../shared/formly/ga-validate.service';
import { IFileUploadFailResponse, IFileUploadResponse, ResponseStatus } from '../../../shared/file-upload/file-upload.service';
import { LinkAddonComponent } from '../../../shared/formly/addons/link-addon.component';
import { GaEventService, IGAEvent } from '../../../shared/services/ga-event.service';
import { Subscription, timer } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { EVENT_USER_PROFILE_UPDATED } from '../../../shared/app-events';
import { SpendLimitInfoComponent } from './spend-limit-info.component';
import { ButtonAddonComponent } from '../../../shared/formly/addons/button-addon.component';
import { Button, Icon } from '../../../shared/app-styles';
import { SSOWarningComponent } from './sso-warning.component';
import { SubcompanyEditComponent } from '../../../shared/company/subcompany-edit.component';
import { LocationEditComponent } from './location-edit.component';
import { ChangePasswordComponent } from './change-password.component';
import { GaWizard } from '../../../shared/wizard/ga-wizard';
import { GaWizardService } from '../../../shared/wizard/ga-wizard.service';
import { GaDeferredService, IGADeferred } from '../../../shared/services/ga-deferred.service';
import { UserProfileStepIds } from '../user-profile-steps';
import { URL_NEW_COMPANY, URL_PRIVACY_STATEMENT } from '../../../shared/browser/url-collections';

const LINKEDIN_URL_REGEX:string = '((https?:\\/\\/)?((www|\\w\\w)\\.)?linkedin\\.com\\/)((([\\w]{2,3})?)|([^\\/]+\\/(([A-zÀ-ÿ0-9_-]|[\\w|\\d-&#?=])+\\/?){1,}))$';

interface IFormFields {
  userInfoFields?:IExtraFieldConfigurationObject[];
  contactDetailsFields?:IExtraFieldConfigurationObject[];
  officeAddressFields?:IExtraFieldConfigurationObject[];
  gaSettingsFields?:IExtraFieldConfigurationObject[];
  profilePictureFields?:IExtraFieldConfigurationObject[];
  commentFields?:IExtraFieldConfigurationObject[];
  spendLimitFields?:IExtraFieldConfigurationObject[];
}

@Component({
  selector: 'user-info',
  template: `
    <div class="row userinfo">
      <div class="col-md-12" *ngIf="profileNotCompleted && !manageExternalUser">
        <div class="alert alert-warning">
          <i class="fa fa-exclamation-triangle"></i>&nbsp;
          <span translate>APP.USER_PROFILE.USER_INFO.PROFILE_INCOMPLETE_NOTICE</span>
        </div>
      </div>
      <div class="col-md-12 inactive-location" *ngIf="model?.officeLocation && !model.officeLocation.active">
        <div class="alert alert-warning">
          <i class="fa fa-exclamation-triangle"></i>&nbsp;
          <span translate>APP.USER_PROFILE.USER_INFO.INACTIVE_LOCATION</span>
        </div>
      </div>

      <div class="col-lg-5 col-md-6 container-1">
        <formly [form]="userInfoForm" [fields]="form.userInfoFields" [model]="model" [noValidate]="true" class="form-horizontal"></formly>
        <formly [form]="contactDetailsForm" [fields]="form.contactDetailsFields" [model]="model" [noValidate]="true" class="form-horizontal"></formly>
        <formly [form]="commentForm" [fields]="form.commentFields" [model]="model" [noValidate]="true" class="form-horizontal"></formly>
        <formly [form]="spendLimitForm" [fields]="form.spendLimitFields" [model]="model" [noValidate]="true" class="form-horizontal"></formly>
      </div>
      <div class="col-lg-5 col-md-6 container-2">
        <formly [form]="officeAddressForm" [fields]="form.officeAddressFields" [model]="model" [noValidate]="true" class="form-horizontal"></formly>
        <formly [form]="gaSettingsForm" [fields]="form.gaSettingsFields" [model]="model" [noValidate]="true" class="form-horizontal"></formly>
      </div>
      <div class="col-lg-2 col-md-6 container-3">
        <formly [form]="profilePictureForm" [fields]="form.profilePictureFields" [model]="model" [noValidate]="true" class="form-horizontal"></formly>
        <img *ngIf="hasProfilePicture" [src]="profilePictureUrl" alt=""/>
        <span *ngIf="!hasProfilePicture" translate>APP.USER_PROFILE.USER_INFO.NO_PICTURE</span>

        <div class="upload-picture">
          <file-button [url]="profilePictureUrl" [autoUpload]="true" [uploading]="isSavingProfilePicture" (onFileAdd)="onProfilePictureAdd()" (onUploadDone)="onProfilePictureUploadDone()" (onUploadFailed)="onProfilePictureUploadFailed($event)">
            <i *ngIf="!isSavingProfilePicture" class="fa fa-folder-open-o">&nbsp;</i>
            <span *ngIf="hasProfilePicture" translate>APP.USER_PROFILE.USER_INFO.CHANGE_PICTURE</span>
            <span *ngIf="!hasProfilePicture" translate>APP.USER_PROFILE.USER_INFO.ADD_PICTURE</span>
          </file-button>
        </div>
      </div>
    </div>
  `
})
export class UserInfoComponent implements IValidatingComponent, OnInit, AfterViewInit, OnDestroy {
  public userInfoForm:UntypedFormGroup = new UntypedFormGroup({});
  public contactDetailsForm:UntypedFormGroup = new UntypedFormGroup({});
  public commentForm:UntypedFormGroup = new UntypedFormGroup({});
  public spendLimitForm:UntypedFormGroup = new UntypedFormGroup({});
  public officeAddressForm:UntypedFormGroup = new UntypedFormGroup({});
  public gaSettingsForm:UntypedFormGroup = new UntypedFormGroup({});
  public profilePictureForm:UntypedFormGroup = new UntypedFormGroup({});

  public form:IFormFields = {};
  public model:IUserInfo;
  public backupModel:IUserInfo;

  private officeCountryField:IExtraFieldConfigurationObject;
  private officeLocationField:IExtraFieldConfigurationObject;
  private themeField:IExtraFieldConfigurationObject;
  private passwordButtonField:IExtraFieldConfigurationObject;
  private editLocationButton:IExtraFieldConfigurationObject;
  private addLocationButton:IExtraFieldConfigurationObject;
  private languageField:IExtraFieldConfigurationObject;
  private companyField:IExtraFieldConfigurationObject;
  private companyDepartmentField:IExtraFieldConfigurationObject;
  private companyTypeRadioField:IExtraFieldConfigurationObject;
  private companyTypeSelectField:IExtraFieldConfigurationObject;
  private subCompanyField:IExtraFieldConfigurationObject;
  private subCompanyGroup:IExtraFieldConfigurationObject;
  private usernameField:IExtraFieldConfigurationObject;
  private ssoInfoField:IExtraFieldConfigurationObject;
  private updateMethodField:IExtraFieldConfigurationObject;
  private emailField:IExtraFieldConfigurationObject;

  public isSavingProfilePicture:boolean = false;
  public hasProfilePicture:boolean;
  public profileNotCompleted:boolean;
  public manageExternalUser:boolean;
  public profilePictureUrl:string;
  public allCountriesPromise:Promise<ICountry[]>;

  private userProfile:IUserData;
  private serverProgressMessage:Noty;
  private webshopId:number;
  private companyId:number;
  private loadingCompanies:boolean;
  private eventHandler:Subscription;

  private changeEmailTimeout:NodeJS.Timeout;
  private wizard:GaWizard;
  private onValidationReadyDeferred:IGADeferred<void>;

  constructor(
    private authService:AuthService,
    private formattingService:FormattingService,
    private formlyService:GaFormlyService,
    private formlyValidatorService:GaFormlyValidatorService,
    private messageService:GaMessageService,
    private countryDataService:CountryDataService,
    private companyDataService:CompanyDataService,
    private translationService:TranslationService,
    private userProfileService:UserProfileService,
    private userProfileDataService:UserProfileDataService,
    private validateService:GaValidateService,
    private languageDataService:LanguageDataService,
    private lovService:LovService,
    private urlService:UrlService,
    private userDataService:UserDataService,
    private gaEventService:GaEventService,
    private wizardService:GaWizardService,
    private changeDetectorRef:ChangeDetectorRef,
    deferredService:GaDeferredService
  ) {
    this.onValidationReadyDeferred = deferredService.defer();
  }

  public ngOnInit():void {
    this.initWizardSetup();

    this.userProfileService.getUserProfilePromise()
      .then(this.initController)
      .then(() => {
        this.listen();
      })
      .then(() => {
        // load latest spend limit info when userprofile is existed (not creating a new user)
        if (this.userProfile?.id) {
          this.userDataService.getSpendLimit(this.userProfile?.id)
            .then((userWebshops: IUserWebshop[]) => {
              if (userWebshops?.length) {
                this.model.userWebshops = (this.model.userWebshops || []).concat(userWebshops);
                // refresh spend limit fields
                this.form.spendLimitFields = this.createSpendLimitFields();
              }
            });
        }
      });
  }

  public ngAfterViewInit():void {
    this.onValidationReadyDeferred.resolve();
  }

  private listen():void {
    this.eventHandler = this.gaEventService.events?.subscribe((event:IGAEvent) => {
      if (event.name === EVENT_USER_PROFILE_UPDATED) {
        this.initController(event.data);
        this.changeDetectorRef.detectChanges();
      }
    });
  }

  private initController = (userData:IUserData):void => {
    this.userProfile = userData;
    this.model = this.userProfileService.getUserInfo();
    this.backupModel = this.userProfileService.getUserInfoBackup(); // initial userInfo model
    this.manageExternalUser = this.userProfileService.manageExternalUser();
    this.hasProfilePicture = this.userProfile.hasProfilePicture;
    this.profilePictureUrl = this.urlService.getProfilePictureUrl(this.model.userId);
    this.profileNotCompleted = (this.userProfile.registrationState === AuthService.STATE_APPROVED);
    this.webshopId = this.userProfileService.getWebshopId();
    this.companyId = this.userProfileService.getCompanyId();

    this.form = this.createFields();

    this.allCountriesPromise = this.countryDataService.getAllCountries();
    this.formlyService.fillField(this.officeCountryField, this.allCountriesPromise)
      .then((countries:ICountry[]) => {
        const countryId:number = this.userProfileService.getCountryId();
        if (countryId) {
          const officeCountry:ICountry = _.find(countries, (country:ICountry) => country.id === countryId);
          this.officeAddressForm.patchValue({ officeCountry });
        }
      });

    if (this.companyTypeRadioField) {
      this.formlyService.fillField([
        this.companyTypeRadioField,
        this.companyTypeSelectField
      ], this.userProfileDataService.getCompanyTypes())
        .then((companyTypes) => {
          const companyType:string = this.model.company?.companyType || this.model.signupCompanyType;
          if (companyType) {
            this.model.companyType = _.find(companyTypes, ct => ct.id === companyType);
            this.updateCompanies();
          }

          this.companyTypeRadioField.hide = companyTypes.length > 3;
          this.companyTypeSelectField.hide = companyTypes.length <= 3;
        });
    }

    if (this.model.registrationState === 'WAIT_APRVL') {
      this.companyField.templateOptions.required = false;
    }

    this.formlyService.fillField(this.languageField, this.languageDataService.getLanguages())
      .then((languages:ILanguage[]) => {
        if(languages.length) {
          this.gaSettingsForm.patchValue({ language: _.findWhere(languages, { id: this.model?.language?.id }) });
        }
      });

    this.updateCompanies();
    this.updateSubCompanies();
    this.updateLocations();
    this.updateDepartments();

    this.onLocationChange();

    if (this.authService.checkAcslUser()) {
      this.formlyService.fillField(this.themeField, this.userProfileDataService.getUserThemes());
    }

    if (!this.authService.checkCustomersUser()) {
      this.formlyService.fillField(this.updateMethodField, this.lovService.list(LOV_UPDATE_METHOD));
    }
  };

  private initWizardSetup():void {
    this.wizard = this.wizardService.getInstance(USERPROFILE_WIZARD);

    // register current validator to use validateCurrent() when saving
    this.wizard.currentValidator(this.validate, true, this.beforeValidate, UserProfileStepIds.USER_INFO);
  }

  public addSubcompany = () => {
    const onClose = ():void => {
      addSubCompanyDialog.content.inputs.busy = false;
      addSubCompanyDialog.hide();
      this.subCompanyField.templateOptions.addOnButton.inputs.busy = () => false;
    };

    const onSave:Function = (model:ISubCompany):void => {
      addSubCompanyDialog.content.inputs.busy = true;

      this.companyDataService.addSubcompany(model)
        .then((subCompany:ISubCompany) => {
          this.model.subCompany = subCompany;
          this.subCompanyField.formControl.patchValue(this.model.subCompany);
          this.updateSubCompanies();
        })
        .catch(util.noop)
        .finally(onClose);
    };

    this.subCompanyField.templateOptions.addOnButton.inputs.busy = () => true;

    const addSubCompanyDialog:IDialog = this.messageService.showDialog({
      component: SubcompanyEditComponent,
      wrapperComponent: false,
      inputs: {
        entity: 'GA.GENERAL.SUBCOMPANY',
        adding: true,
        model: <any>{
          company: this.model.company,
          active: true
        }
      },
      outputs: {
        onSave,
        onCancel: onClose
      },
      size: DialogSize.MD
    });
  };

  public addEditLocation = (newLocation?:boolean):void => {
    if (this.model.company) {
      const getLocationModel:Function = ():ILocationExtended => {
        let locationModel:ILocationExtended = util.deepClone(this.model.officeLocation);
        if (newLocation) {
          locationModel = <any>{
            companyCountry: {},
            subCompany: this.model.subCompany,
            country: this.model.officeCountry
          };
        } else {
          if (this.model.officeLocation?.companyCountry?.country) {
            locationModel.country = util.deepClone(this.model.officeLocation.companyCountry.country);
          }
        }
        return locationModel;
      };
      const locationModel:ILocationExtended = getLocationModel();

      const updateButtonStatus:Function = (status:boolean):void => {
        const button:IExtraFieldConfigurationObject = newLocation? this.addLocationButton : this.editLocationButton;
        button.templateOptions.busy = status;
      };

      const onClose:Function = ():void => {
        addEditLocationDialog.hide();
        updateButtonStatus(false);
      };

      const onSave:Function = ():void => {
        const afterLocationSaved:Function = (location:ILocationExtended):void => {
          // update model
          this.officeLocationField.formControl.patchValue(location);

          this.updateLocations();

          // close dialog
          onClose();
        };

        const locationRequest:ILocationExtended = util.deepClone(locationModel);
        locationRequest.companyCountry = locationRequest.companyCountry || <any>{};
        locationRequest.companyCountry.country = <ICountryExtended>locationRequest.country;

        // the company cannot be updated
        if (locationRequest.companyCountry.company) {
          delete locationRequest.companyCountry.company;
        }

        addEditLocationDialog.content.inputs.busy = true;
        if (locationModel.id) {
          this.userProfileDataService.editLocation(this.model.company.id, this.model.officeLocation.id, locationRequest)
            .then((location:ILocationExtended) => {
              afterLocationSaved(location);
            })
            .finally(() => {
              addEditLocationDialog.content.inputs.busy = false;
            });
        } else {
          this.userProfileDataService.addLocation(this.model.company.id, locationRequest)
            .then((location:ILocationExtended) => {
              afterLocationSaved(location);
            })
            .finally(() => {
              addEditLocationDialog.content.inputs.busy = false;
            });
        }
      };

      updateButtonStatus(true);
      const addEditLocationDialog:IDialog = this.messageService.showDialog({
        component: LocationEditComponent,
        wrapperComponent: false,
        inputs: {
          entity: 'GA.GENERAL.LOCATION',
          adding: newLocation,
          model: locationModel,
          customerSubCompanyMandatory: this.model.company.subCompanyMandatory,
          subCompaniesPromise: this.companyDataService.getSubCompanies(this.model.company.id),
          allCountriesPromise: this.allCountriesPromise
        },
        outputs: {
          onSave,
          onCancel: onClose
        },
        size: DialogSize.MD
      });
    }
  };

  public sendResetLink = ():void => {
    setTimeout(() => { // this waits for username to be set in the model
      if (!this.model.username || this.model.username && this.model.username !== this.userProfile.login) {
        this.messageService.showWarningDialog('APP.USER_PROFILE.USER_INFO.WARNING_USERNAME_CHANGED.TITLE', 'APP.USER_PROFILE.USER_INFO.WARNING_USERNAME_CHANGED.TEXT');
      } else {
        this.passwordButtonField.templateOptions.busy = true;
        this.passwordButtonField.templateOptions.disabled = true;
        this.userProfileDataService.resetPassword(this.userProfileService.getUserProfileId())
          .then(() => {
            // feedback for 5 sec
            this.messageService.showFeedbackMessage('APP.USER_PROFILE.USER_INFO.PASSWORD_RESET_LINK_SENT');
            this.usernameField.templateOptions.disabled = true;
          })
          .finally(() => {
            this.passwordButtonField.templateOptions.busy = false;
            this.passwordButtonField.templateOptions.disabled = false;
          });
      }
    }, 250);
  };

  public changePassword = ():void => {
    const onClose = ():void => {
      changePasswordDialog.hide();
      this.passwordButtonField.templateOptions.busy = false;
    };

    const onSave = (token:string):void => {
      this.authService.setToken(token);
      onClose();
      this.messageService.showFeedbackMessage('APP.USER_PROFILE.USER_INFO.PASSWORD_CHANGED');
    };

    this.passwordButtonField.templateOptions.busy = true;
    const changePasswordDialog:IDialog = this.messageService.showDialog({
      component: ChangePasswordComponent,
      wrapperComponent: false,
      inputs: {
        entity: 'APP.USER_PROFILE.USER_INFO.PASSWORD'
      },
      outputs: {
        onSave,
        onCancel: onClose
      },
      size: DialogSize.MD
    });
  };

  private getLinkedinUrl(value:string):string {
    let result:string;

    if (value) {
      // this case when model contains only username
      if (value.indexOf('/') === -1) {
        result = `https://www.linkedin.com/in/${value}`;
      }
        // this case when model not contains protocol (http or https)
      // e.g. linkedin.com/in/triadiprabowo, www.linkedin.com/in/triadiprabowo
      else if (!value.match(/^https?:\/\//i) && value.match(LINKEDIN_URL_REGEX)) {
        result = `https://${value}`;
      }
      // return as it is, let the LinkedIn url validation do the works
      else {
        result = value;
      }
    }

    return result;
  }

  // these 2 methods are only executed from the component userprofile.component.ts method saveProfile()
  //noinspection JSUnusedGlobalSymbols
  public beforeValidate = ():Promise<boolean> => {
    return this.onValidationReadyDeferred.promise
      .then(() => this.userProfileService.getUserProfilePromise()
      .then((userData:IUserData) => !!userData));
  };
  //noinspection JSUnusedGlobalSymbols
  public validate = (context:IValidationContext):Promise<void|string[]> => {
    const errorMessages:string[] = [];
    if (context?.type === 'APPROVE' && !this.model.company) { // also verify that there is a company
      errorMessages.push(this.translationService.instant('GA.FORM.REQUIRED', { label: this.translationService.instant('GA.GENERAL.COMPANY') }));
    }

    return this.validateService.validateFields(
      this.form.userInfoFields.concat(
        this.form.contactDetailsFields,
        this.form.officeAddressFields,
        this.form.gaSettingsFields,
        this.form.profilePictureFields
      ),
      this.model,
      true,
      errorMessages
    );
  };

  private createFields():IFormFields {
    const form:IFormFields = {
      userInfoFields: [],
      contactDetailsFields: [],
      officeAddressFields: [],
      gaSettingsFields: [],
      profilePictureFields: [],
      commentFields: [],
      spendLimitFields: []
    };

    const labelClass:string = 'col-md-4';
    const valueClass:string = 'col-md-8';
    let linkedin:IExtraFieldConfigurationObject;

    // User info
    form.userInfoFields = [
      this.formlyService.aTitle('APP.USER_PROFILE.HEADINGS.USER_INFO')
        .build(),

      this.formlyService.aValue('userId', 'GA.GENERAL.ID')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withHideExpression(() => !this.model.userId)
        .build()
    ];

    if (this.manageExternalUser) {
      form.userInfoFields.push(
        this.formlyService.aValue('registrationState', 'Registration state')
          .withLabelClass(labelClass)
          .withValueClass(valueClass)
          .withHideExpression(() => !this.model.registrationState)
          .build()
      );
    }

    this.usernameField = this.formlyService.anInput('username', 'APP.USER_PROFILE.USER_INFO.USERNAME')
      .isRequired()
      .withLabelClass(labelClass)
      .withValueClass(valueClass)
      .withHideExpression(() => this.isSSOOnly())
      .withExtraOptions({
        asyncValidators: {
          uniqueUsername: {
            expression: (control:UntypedFormControl) => {
              // debounce 250ms
              return timer(250).pipe(switchMap(() => {
                if (control.value !== this.backupModel.username && (!this.model.userId || !this.usernameField.formControl || this.usernameField.formControl?.dirty)) { // only check when it's a new user or if the user has typed something
                  return this.userProfileDataService.checkUsernameAvailable(control.value);
                }
                return Promise.resolve(true);
              }));
            },
            message: this.translationService.instant('APP.ACCESS.SIGNUP.USERNAME_EXISTS')
          }
        }
      })
      .build();

    form.userInfoFields = form.userInfoFields.concat([
      this.formlyService.anInput('firstName', 'GA.GENERAL.FIRSTNAME')
        .isRequired()
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .build(),

      this.formlyService.anInput('lastName', 'GA.GENERAL.LASTNAME')
        .isRequired()
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .build(),

      this.emailField = this.formlyService.anInput('email', 'GA.GENERAL.EMAIL')
        .isRequired()
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .onChange(this.onChangeEmail)
        .withExtraOptions({
          validators: {
            email: this.formlyValidatorService.multipleEmailValidator
          },
          asyncValidators: {
            uniqueEmail: {
              expression: (control:UntypedFormControl) => {
                // debounce 250ms
                return timer(250).pipe(switchMap(() => {
                  if (control.value !== this.backupModel.email && this.model.company?.onlySSO && (!this.model.userId || !this.emailField.formControl || this.emailField.formControl?.dirty)) { // only check when it's a new user or if the user has typed something and company is SSO only
                    return this.userProfileDataService.checkEmailAvailable(control.value);
                  }
                  return Promise.resolve(true);
                }));
              },
              message: this.translationService.instant('APP.ACCESS.SIGNUP.EMAIL_EXISTS_SSO')
            }
          }
        })
        .withInfo(this.translationService.instant('GA.INFO.MULTIPLE_EMAILS'))
        .build(),

      this.usernameField
    ]);

    const createPasswordButtonField:Function = (field:IExtraFieldConfigurationObject):IExtraFieldConfigurationObject => {
      return this.formlyService.aGroup('password', 'APP.USER_PROFILE.USER_INFO.PASSWORD')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withHideExpression(() => this.isSSOOnly())
        .withFields([field])
        .build();
    };

    if (this.model.userId && this.userProfile.registrationState !== AuthService.STATE_REJECTED) {
      if (this.manageExternalUser) {
        this.passwordButtonField = this.formlyService.aLink('APP.USER_PROFILE.USER_INFO.SEND_PASSWORD_RESET_BUTTON')
          .withWrapperClass('col-sm-12')
          .withLabelClass('d-none')
          .withValueClass('col-md-12')
          .withExtraOptions({
            templateOptions: {
              linkClass: 'btn-default no-margin'
            }
          })
          .onClick(this.sendResetLink)
          .build();
        form.userInfoFields.push(createPasswordButtonField(this.passwordButtonField));
      } else if (this.profileNotCompleted) {
        const passwordField:IExtraFieldConfigurationObject = this.formlyService.anInput('password', 'APP.USER_PROFILE.USER_INFO.PASSWORD')
          .isRequired()
          .withLabelClass(labelClass)
          .withValueClass(valueClass)
          .withInputType('password')
          .withMinLength(6)
          .build();

        const confirmPasswordField:IExtraFieldConfigurationObject = this.formlyService.anInput('confirmPassword', 'APP.USER_PROFILE.USER_INFO.CONFIRM_PASSWORD')
          .isRequired()
          .withLabelClass(labelClass)
          .withValueClass(valueClass)
          .withInputType('password')
          .withMinLength(6)
          .withExtraOptions({
            validators: {
              format: {
                expression: (control:UntypedFormControl) => this.model.password === control?.value,
                message: this.translationService.instant('APP.ACCESS.PASSWORD.PASSWORDS_NOT_MATCH')
              }
            }
          })
          .build();

        if(!this.isSSOOnly()) {
          form.userInfoFields.push(passwordField, confirmPasswordField);
        }
      } else {
        this.passwordButtonField = this.formlyService.aLink('APP.USER_PROFILE.USER_INFO.CHANGE_PASSWORD_BUTTON')
          .withWrapperClass('col-sm-12')
          .withLabelClass('d-none')
          .withValueClass('col-md-12')
          .withExtraOptions({
            templateOptions: {
              linkClass: 'btn-default no-margin'
            }
          })
          .onClick(this.changePassword)
          .build();

        form.userInfoFields.push(
          createPasswordButtonField(this.passwordButtonField)
        );
      }
    }

    form.userInfoFields.push(
      this.formlyService.anInput('jobTitle', 'APP.USER_PROFILE.USER_INFO.JOB_TITLE')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .build(),
      linkedin = this.formlyService.anInput('linkedin', 'Linkedin')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withAddOnButton({
          component: LinkAddonComponent,
          inputs: {
            icon: 'fa-external-link',
            href: this.model.linkedin || '',
            linkClass: 'btn btn-default',
            disabled: () => linkedin.formControl.invalid || !this.model.linkedin,
            title: 'Open linkedin profile'
          }
        })
        .onBlur(() => this.model.linkedin = this.getLinkedinUrl(this.model.linkedin))
        .withPattern(LINKEDIN_URL_REGEX, 'Linkedin URL is not valid')
        .build(),

      this.formlyService.aGroup('signature', 'APP.USER_PROFILE.USER_INFO.SIGNATURE')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withHideExpression(() => this.manageExternalUser || !this.authService.checkAcslUser())
        .withFields([
          this.formlyService.aLink('APP.USER_PROFILE.USER_INFO.COPY_SIGNATURE')
            .withWrapperClass('col-sm-12')
            .withLabelClass('d-none')
            .withValueClass('col-md-12')
            .withExtraOptions({
              templateOptions: {
                linkClass: 'btn-default'
              }
            })
            .onClick(() => this.copySignature(this.model))
            .build()
        ])
        .build()
    );


    // Contact details
    form.contactDetailsFields = [
      this.formlyService.aTitle('APP.USER_PROFILE.HEADINGS.CONTACT_DETAILS')
        .build(),

      this.formlyService.anInput('directPhone', 'GA.GENERAL.PHONE_DIRECT')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .build(),

      this.formlyService.anInput('officePhone', 'GA.GENERAL.PHONE_OFFICE')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .build(),

      this.formlyService.anInput('cellPhone', 'GA.GENERAL.PHONE_CELL')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .build(),

      this.formlyService.anInput('fax', 'GA.GENERAL.FAX')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .build(),

      this.formlyService.anInput('preferredCommunication', 'APP.USER_PROFILE.USER_INFO.PREFERRED_COMMUNICATION')
        .withInfo(this.translationService.instant('APP.USER_PROFILE.USER_INFO.PREFERRED_COMMUNICATION_INFO'))
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .build(),

      this.formlyService.anInput('whatsAppId', 'APP.USER_PROFILE.USER_INFO.WHATSAPP_ID')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .build(),

      this.formlyService.anInput('weChatId', 'APP.USER_PROFILE.USER_INFO.WECHAT_ID')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .build()
    ];

    if (this.authService.checkAcslUser()) {
      form.contactDetailsFields.push(
        this.formlyService.aSelect('fluentEnglish', 'APP.USER_PROFILE.USER_INFO.FLUENT_IN_ENGLISH')
          .withLabelClass(labelClass)
          .withValueClass(valueClass)
          .withFormat(this.formattingService.formatBoolean)
          .withOptions(<IValueObject[]>this.formlyService.getBooleanOptions())
          .build()
      );
    }

    let roleField:IExtraFieldConfigurationObject;

    // Office address
    if (!this.userProfile.location?.companyCountry?.company || this.userProfile.companyEditable) {
      let info:string = '';
      if (this.model.signupCompany) {
        info += `User provided company name '${this.model.signupCompany}<br />'`;
      }
      if (this.authService.checkAcslUser()) {
        info += `If needed, you can <a href="${URL_NEW_COMPANY}" target="_blank">create a new company</a>. Make sure to reload the company list afterwards`;
      }

      this.companyField = this.formlyService.aSelect('company', 'GA.GENERAL.COMPANY')
        .isRequired()
        .withLoading(false)
        .withFormat(this.formattingService.formatCompany)
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withAddOnButton({
          component: ButtonAddonComponent,
          inputs: {
            icon: Icon.REFRESH,
            buttonClass: Button.DEFAULT,
            title: 'GA.GENERAL.COMPANY',
            busy: () => false
          },
          outputs: {
            onClick: () => {
              this.companyField.templateOptions.addOnButton.inputs.busy = () => true;
              this.updateCompanies(true)
                .finally(() => {
                  this.companyField.templateOptions.addOnButton.inputs.busy = () => false;
                });
            }
          }
        })
        .withInfo(info)
        .showInfo()
        .onChange((company:ICompany) => {
          // clear the currently selected generic user
          this.model.genericUser = undefined;
          this.onCompanySelection(company);
          this.updateSubCompanies();
          this.updateLocations();
          this.updateDepartments();
        })
        .build();

      if (!this.webshopId && !this.companyId) {
        this.companyTypeRadioField = this.formlyService.aRadio('companyType', 'GA.GENERAL.COMPANY_TYPE')
          .isRequired()
          .onChange(() => this.updateCompanies())
          .withFormat(this.formattingService.formatLov)
          .withLabelClass(labelClass)
          .withValueClass(valueClass)
          .build();

        this.companyTypeSelectField = this.formlyService.aSelect('companyType', 'GA.GENERAL.COMPANY_TYPE')
          .isRequired()
          .onChange(() => this.updateCompanies())
          .withFormat(this.formattingService.formatLov)
          .withLabelClass(labelClass)
          .withValueClass(valueClass)
          .isHide()
          .build();

        roleField = this.formlyService.aValue('signupRole', 'APP.ACCESS.SIGNUP.ACCOUNT_TYPE')
          .withLabelClass(labelClass)
          .withValueClass(valueClass)
          .withHideExpression(() => !this.model.signupRole)
          .build();
      }
    } else {
      this.companyField = this.formlyService.aValue('company', 'GA.GENERAL.COMPANY')
        .withFormat(this.formattingService.formatCompany)
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .build();
    }

    const companyDepartmentFieldRO:IExtraFieldConfigurationObject = this.formlyService.aValue('companyDepartment', 'APP.USER_PROFILE.USER_INFO.DEPARTMENT_COST_CENTRE')
      .withHideExpression(() => !this.model.companyDepartment || this.model.company?.name === 'ACSL')
      .withFormat(this.formattingService.formatDepartment)
      .withLabelClass(labelClass)
      .withValueClass(valueClass)
      .build();

    if (this.manageExternalUser || this.authService.checkAcslUser()) {
      this.subCompanyField = this.formlyService.aSelect('subCompany', 'GA.GENERAL.SUBCOMPANY')
        .withFormat(this.formattingService.formatCompany)
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withAddOnButton({
          component: ButtonAddonComponent,
          inputs: {
            icon: Icon.PLUS,
            buttonClass: Button.DEFAULT,
            title: 'Create a new subcompany',
            busy: () => false
          },
          outputs: {
            onClick: this.addSubcompany
          }
        })
        .onChange(() => {
          this.updateLocations();
        })
        .build();
      if (this.model.signupSubCompany) {
        this.subCompanyField.templateOptions.info = `User provided sub company name '${this.model.signupSubCompany}'`;
        this.subCompanyField.templateOptions.showInfo = true;
      }
      this.subCompanyGroup = this.subCompanyField;

      this.companyDepartmentField = this.formlyService.aSelect('companyDepartment', 'Department')
        .withHideExpression(() => !this.model.company || this.model.company.name !== 'ACSL')
        .withFormat(this.formattingService.formatDepartment)
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .build();

    } else {
      this.subCompanyField = this.formlyService.aValue('subCompany', 'GA.GENERAL.SUBCOMPANY')
        .withFormat(this.formattingService.formatCompany)
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withValidationMessage('required', this.translationService.instant('GA.VALIDATION.REQUIRED', { label: 'GA.GENERAL.SUBCOMPANY' }))
        .build();
      this.subCompanyGroup = this.subCompanyField;
    }
    // hide subcompany when: no company has been selected OR selected company has no subcompanies
    this.subCompanyGroup.hide = !this.model.company || this.model.company && !this.model.company.hasSubCompanies;
    // make subcompany required when company has mandatory subcompanies
    this.subCompanyGroup.templateOptions.required = this.subCompanyField.templateOptions.required = this.model.company?.subCompanyMandatory;

    this.officeCountryField = this.formlyService.aSelect('officeCountry', 'APP.USER_PROFILE.USER_INFO.SEARCH_BY_COUNTRY')
      .withLoading(false)
      .withFormat(this.formattingService.formatCountry)
      .withLabelClass(labelClass)
      .withValueClass(valueClass)
      .onChange(() => {
        this.updateSubCompanies();
        this.updateLocations();
      })
      .build();

    this.officeLocationField = this.formlyService.aSelect2('officeLocation', 'GA.GENERAL.LOCATION')
      .withRequired(!this.manageExternalUser || this.userProfile.registrationState === AuthService.STATE_REGISTERED)
      .withLabelClass(labelClass)
      .withValueClass(valueClass)
      .withInfo(this.translationService.instant('APP.USER_PROFILE.USER_INFO.LOCATION_FIELD_INFO'))
      .showInfo()
      .withFormat(this.formattingService.formatLocationHtml)
      .withExtraOptions({
        templateOptions: {
          parseHtml: true,
          showEmptyOption: true
        }
      })
      .onChange(this.onLocationChange)
      .build();

    this.ssoInfoField = this.formlyService.aCustom()
      .withComponent(SSOWarningComponent)
      .withExtraOptions({
        templateOptions: {
          inputs: {
            urlSSOLogin: () => this.model?.company?.urlSSOLogin
          }
        }
      })
      .withHideExpression(() => !this.manageExternalUser || !this.model.company?.useSSO)
      .withLabelClass('d-none')
      .withValueClass('col-md-12')
      .build();

    form.officeAddressFields = [
      this.formlyService.aTitle('APP.USER_PROFILE.HEADINGS.OFFICE_ADDRESS')
        .build(),
      this.companyTypeRadioField,
      this.companyTypeSelectField,
      roleField,
      this.companyField,
      this.companyDepartmentField,
      companyDepartmentFieldRO,
      this.ssoInfoField,
      this.subCompanyGroup,
      this.officeCountryField,
      this.officeLocationField,

      this.formlyService.aValue('officeAddress', 'GA.GENERAL.ADDRESS')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .build(),

      this.formlyService.anInput('office', 'GA.GENERAL.OFFICE')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .build()
    ].filter(field => field); //remove undefineds

    this.editLocationButton = this.formlyService.aLink('GA.LOCATION.EDIT.EDIT_LOCATION')
      .isDisabled()
      .withExtraOptions({ templateOptions: { linkClass: 'btn-default' } })
      .onClick(() => this.addEditLocation())
      .withWrapperClass('col text-end')
      .withLabelClass('d-none')
      .withValueClass('col')
      .build();

    this.addLocationButton = this.formlyService.aLink('GA.LOCATION.EDIT.ADD_LOCATION')
      .isDisabled()
      .withExtraOptions({ templateOptions: { linkClass: 'btn-primary' } })
      .onClick(() => this.addEditLocation(true))
      .withWrapperClass('col-auto text-end ms-1')
      .withLabelClass('d-none')
      .withValueClass('col')
      .build();

    form.officeAddressFields.push(
      this.formlyService.aGroup(undefined)
        .withFields([
          this.addLocationButton,
          this.editLocationButton
        ])
        .withLabelClass('d-none')
        .withValueClass('col-md-12')
        .withExtraOptions({
          templateOptions: {
            fieldGroupWrapperClass: 'flex-row-reverse g-0'
          }
        })
        .build()
    );

    // GA settings
    if (this.authService.checkAcslUser()) {
      this.themeField = this.formlyService.aSelect('gaLayout', 'GA.GENERAL.THEME')
        .withLoading(false)
        .withFormat(this.formattingService.formatLov)
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .build();
    }

    this.languageField = this.formlyService.aSelect('language', 'GA.GENERAL.LANGUAGE')
      .isRequired()
      .withLoading(true)
      .withFormat(this.formattingService.formatLanguage)
      .withLabelClass(labelClass)
      .withValueClass(valueClass)
      .build();

    form.gaSettingsFields = [
      this.formlyService.aTitle('APP.USER_PROFILE.HEADINGS.GA_SETTINGS')
        .build(),
      this.languageField
    ];
    if (this.themeField) {
      form.gaSettingsFields.push(this.themeField);
    }

    if (!this.authService.checkCustomersUser()) {
      form.gaSettingsFields.push(
        this.updateMethodField = this.formlyService.aSelect('updateMethod', 'APP.USER_PROFILE.USER_INFO.UPDATE_METHOD')
          .withLabelClass(labelClass)
          .withValueClass(valueClass)
          .withFormat(this.formattingService.formatLov)
          .build(),

        this.formlyService.anInput('updateComments', 'APP.USER_PROFILE.USER_INFO.UPDATE_COMMENTS')
          .withLabelClass(labelClass)
          .withValueClass(valueClass)
          .withInfo('APP.USER_PROFILE.USER_INFO.UPDATE_COMMENTS_INFO')
          .showInfo()
          .build(),

        this.formlyService.anInput('updateFrequency', 'APP.USER_PROFILE.USER_INFO.UPDATE_FREQUENCY')
          .withLabelClass(labelClass)
          .withValueClass(valueClass)
          .withInfo('APP.USER_PROFILE.USER_INFO.UPDATE_FREQUENCY_INFO')
          .showInfo()
          .build(),

        this.formlyService.anInput('timeSlotForGAUpdates', 'APP.USER_PROFILE.USER_INFO.TIMESLOT_GA_UPDATES')
          .withLabelClass(labelClass)
          .withValueClass(valueClass)
          .withInfo('APP.USER_PROFILE.USER_INFO.TIMESLOT_GA_UPDATES_INFO')
          .showInfo()
          .build(),

        this.formlyService.anInput('checkpoint', 'APP.USER_PROFILE.USER_INFO.CHECKPOINT')
          .withLabelClass(labelClass)
          .withValueClass(valueClass)
          .withInfo('APP.USER_PROFILE.USER_INFO.CHECKPOINT_INFO')
          .showInfo()
          .build()
      );
    }

    if (this.authService.checkAcslUser()) {
      form.gaSettingsFields.push(
        this.formlyService.aGroup('Single Sign-On')
          .withFields([
            this.formlyService.aCheckbox('localCredentialsAlwaysAllowed', 'APP.USER_PROFILE.USER_INFO.CAN_SET_PASSWORD')
              .withWrapperClass('col-md-12')
              .withoutWrapper()
              .onChange(() => this.updateUsernameField())
              .build()
          ])
          .withLabelClass(labelClass)
          .withValueClass(valueClass)
          .withHideExpression(() => !this.model.company?.useSSO || this.model.company?.onlySSO)
          .build()
      );
    }

    if (!this.manageExternalUser) {
      const link:string = `<a href="${URL_PRIVACY_STATEMENT}" target="_blank">${this.translationService.instant('GA.GENERAL.PRIVACY_STATEMENT')}</a>`;
      const privacyAgreedLabel:string = this.translationService.instant('APP.USER_PROFILE.USER_INFO.PRIVACY_STATEMENT_FIELD', { link });
      form.gaSettingsFields.push(
        this.formlyService.aCheckbox('privacyAgreed', privacyAgreedLabel)
          .isRequired()
          .withoutWrapper()
          .withValidationMessage('required', this.translationService.instant('GA.GENERAL.PRIVACY_STATEMENT_ERROR'))
          .build()
      );
    }

    // Profile picture
    form.profilePictureFields = [
      this.formlyService.aTitle('APP.USER_PROFILE.HEADINGS.PROFILE_PICTURE')
        .build()
    ];

    form.commentFields = [
      this.formlyService.aTitle('APP.USER_PROFILE.HEADINGS.COMMENTS')
        .build(),
      this.formlyService.aTextarea('comment', '')
        .withoutWrapper()
        .build()
    ];

    // if user has access to certain webshop(s) with spend limit then render spend limit info section
    if (this.model.userWebshops?.length > 0) {
      form.spendLimitFields = this.createSpendLimitFields();
    }

    return form;
  }

  private createSpendLimitFields(): IExtraFieldConfigurationObject[] {
    return [
      this.formlyService.aTitle('APP.USER_PROFILE.HEADINGS.SPEND_LIMIT')
        .build(),

      this.formlyService.aCustom()
        .withoutWrapper()
        .withComponent(SpendLimitInfoComponent)
        .withExtraOptions({
          templateOptions: {
            inputs: {
              userWebshops: this.model.userWebshops
            }
          }
        })
        .build()
    ];
  }

  private isSSOOnly():boolean {
    return this.model.company?.onlySSO || (this.model.company?.useSSO && !this.model.localCredentialsAlwaysAllowed);
  }

  private onChangeEmail = () => {
    if (this.usernameField.hide) {
      return;
    }

    if (!this.model.userId && this.usernameField.formControl?.untouched) {
      clearTimeout(this.changeEmailTimeout);
      this.changeEmailTimeout = setTimeout(() => {
        this.usernameField.formControl.setValue(this.model.email); // this will also run the unique username validation
        this.usernameField.formControl.markAsDirty(); // make sure it shows up
      }, 250);
    }
  };

  private onCompanySelection(company:ICompany):void {
    // load a different list of generic users
    this.userProfileService.loadGenericUsers();

    if (company) {
      this.subCompanyField.templateOptions.required = company.subCompanyMandatory;
      this.subCompanyGroup.hide = !this.model.company.hasSubCompanies;
      this.updateUsernameField();
    }
  }

  private updateCompanies = (reload?:boolean):Promise<any> => {
    if (this.model.companyType || this.webshopId || this.companyId) {
      this.loadingCompanies = true;
      return this.formlyService.fillField(
        this.companyField,
        this.userProfileDataService.getCompanies(this.model.companyType, this.webshopId, this.companyId, reload)
      )
        .then((companies:ICompany[]) => {
          // when only one company -> preselect it
          if (companies.length === 1 && !this.model.company) {
            this.companyField.formControl.setValue(companies[ 0 ]);
          }
          return companies;
        })
        .finally(() => {
          this.loadingCompanies = false;
        });
    }

    return Promise.resolve();
  };

  private updateDepartments = ():void => {
    if (this.model.company?.name === 'ACSL' && this.authService.checkAcslUser()) {
      this.formlyService.fillField(
        this.companyDepartmentField,
        this.companyDataService.getCompanyDepartments(this.model.company.id)
      );
    }
  };

  private updateSubCompanies = ():void => {
    if (this.model.company?.hasSubCompanies) {
      if (this.manageExternalUser) {
        this.formlyService.fillField(
          this.subCompanyField,
          this.companyDataService.getSubCompanies(this.model.company.id)
        );
      }
    } else {
      this.model.subCompany = undefined;
      this.subCompanyField.templateOptions.options = [];
    }
  };

  private onLocationChange = ():void => {
    const selectedLocation:ILocationExtended = this.model.officeLocation;
    if (selectedLocation && selectedLocation.companyCountry?.country?.id !== 999) {
      this.model.officeAddress = this.formattingService.formatLocationHtml(selectedLocation, {
        name: selectedLocation.companyCountry?.country?.vatName,
        number: selectedLocation.vatNumber
      });
      this.editLocationButton.templateOptions.disabled = !selectedLocation.editable;
      if (selectedLocation.subCompany) {
        this.model.subCompany = selectedLocation.subCompany;
        if (this.subCompanyField?.formControl) {
          this.subCompanyField.formControl.patchValue(selectedLocation.subCompany);
        }
      }
    } else {
      this.model.officeAddress = undefined;
      this.editLocationButton.templateOptions.disabled = true;
    }
    this.officeAddressForm.patchValue({ officeAddress: this.model.officeAddress });
  };

  private updateLocations = ():void => {
    if (this.locationUpdateAllowed()) {
      this.formlyService.fillField(this.officeLocationField,
        this.userProfileDataService.getUserLocations(
          this.model.officeCountry.id,
          this.model.company.id,
          (this.model.company.useSSO? undefined : this.model.subCompany?.id))); // if the company uses SSO, we always load a list of all subcompany locations
    } else {
      this.officeLocationField.templateOptions.options = [];
    }
    // enable/disable the button 'Add Location'
    this.addLocationButton.templateOptions.disabled = !(this.model.company && this.model.officeCountry);
  };

  private locationUpdateAllowed(): boolean {
    return this.model.company                                                 // we need a company
      && this.model.officeCountry?.id && this.model.officeCountry.id !== 999  // we need a 'real' country
      && ((!this.model.subCompany && (this.model.company.useSSO || !this.model.company.subCompanyMandatory)) // when subcompany not mandatory or company uses SSO, we don't need a subcompany
        || (this.model.subCompany && this.model.company.hasSubCompanies));    // otherwise we do
  }

  public onProfilePictureAdd = ():void => {
    this.isSavingProfilePicture = true;
    this.serverProgressMessage = this.messageService.showStatusMessage(this.translationService.instant('APP.USER_PROFILE.USER_INFO.UPLOADING_PICTURE'));
  };

  public onProfilePictureUploadFailed = (response?:IFileUploadFailResponse | IFileUploadResponse):void => {
    this.uploadProfilePictureFinished();
    this.hasProfilePicture = false;
    let errorMessage:string = `Error ${this.translationService.instant('APP.USER_PROFILE.USER_INFO.UPLOADING_PICTURE')}`;
    if (response?.status === ResponseStatus.FAIL) {
      errorMessage += (<IFileUploadFailResponse>response).error? ': ' + (<IFileUploadFailResponse>response).error : '';
    }
    this.messageService.showErrorMessage(errorMessage);
  };

  public onProfilePictureUploadDone():void {
    this.uploadProfilePictureFinished();
    this.hasProfilePicture = true;
    this.profilePictureUrl = this.urlService.getProfilePictureUrl(this.model.userId, true);
  }

  private uploadProfilePictureFinished = ():void => {
    this.isSavingProfilePicture = false;
    this.messageService.closeMessage(this.serverProgressMessage);
  };

  private updateUsernameField():void {
    if (!this.userProfile?.id) {
      if (this.isSSOOnly()) {
        this.backupModel.username = this.usernameField.formControl.value;
        this.model.username = undefined;
      } else {
        if (this.backupModel.username) {
          this.model.username = this.backupModel.username;
        }
      }
    }
  }

  public async copySignature(model:IUserInfo) {
    const logoUrl:string = '/unacc/layout/assets/acsl.logo.top.png';
    try {
      const imageData:Response = await fetch(logoUrl);
      const imageBlob:Blob = await imageData.blob();
      const imageBase64Data:string|ArrayBuffer = await util.blobToBase64(imageBlob);
      const contact:IContactExtended = {
        id: model.userId,
        firstname: model.firstName,
        lastname: model.lastName,
        title: model.jobTitle,
        companyDepartment: model.companyDepartment,
        company: model.company.name,
        email: model.email,
        whatsappId: model.whatsAppId,
        weChatId: model.weChatId,
        timezone: model.timezone,
        cellPhone: model.cellPhone
      };

      const html:BlobPart =
        `<p>${this.formattingService.formatContactSignature(contact)}</p>
        <p>
          <a href="https://ga.acslworld.com" target="_blank">
            <img src="${imageBase64Data}" alt="ACSL logo" />
          </a>
        </p>`;

      const textBlob:Blob = new Blob([html], { type: 'text/html' });
      await (<any>navigator.clipboard).write([
        // @ts-ignore
        new ClipboardItem({
          [textBlob.type]: textBlob
        })
      ]);

      this.messageService.showFeedbackMessage('Copied to clipboard');

    } catch(err) {
      console.error('Failed to copy the signature', err.name, err.message);
    }
  }

  public ngOnDestroy():void {
    this.wizard?.clearCurrentValidator();
    this.eventHandler?.unsubscribe();
  }
}